import AdvancedSelect from "@edaz/edazkit/controls/AdvancedSelect";
import FakeInput from "@edaz/edazkit/controls/FakeInput";
import { formatBankAccount } from "@edaz/utils/bankAccount";
import { Check, Input } from "@frui.ts/bootstrap";
import type { ViewComponent } from "@frui.ts/views";
import BaseAccountNumberInput from "controls/finance/baseAccountNumberInput";
import CzechAccountInput from "controls/finance/czechAccountInput";
import IbanInput from "controls/finance/ibanInput";
import FormGroup from "controls/formGroup";
import FormLabel from "controls/formLabel";
import CustomerRefundCandidateCounterpartyBankResponse from "entities/customerRefundCandidateCounterpartyBankResponse";
import FeeType from "entities/feeType";
import RefundPaymentType from "entities/refundPaymentType";
import { observer } from "mobx-react-lite";
import React from "react";
import { Col, Form } from "react-bootstrap";
import { formatAmount, translateEnum, translateGeneral } from "utils/localizations";
import CustomerRefundCandidateDetailViewModel from "viewModels/finance/customerRefunds/customerRefundCandidates/customerRefundCandidateDetailViewModel";
import type RejectedCustomerRefundCandidateDetailViewModel from "viewModels/finance/customerRefunds/rejectedCustomerRefundCandidates/rejectedCustomerRefundCandidateDetailViewModel";
import BankCodeInputGroup from "views/common/bankCodeInputGroup";
import CountrySelect from "views/common/countrySelect";
import PossibleSEPAPaymentWarning from "../../../common/possibleSEPAPaymentWarning";

const BankTransferRefundSection: ViewComponent<
  CustomerRefundCandidateDetailViewModel | RejectedCustomerRefundCandidateDetailViewModel
> = observer(({ vm }) => {
  const refundPayment = vm.item.refundPayment;
  const paymentType = refundPayment.paymentType;
  const counterpartyBank = refundPayment.counterpartyBank ?? new CustomerRefundCandidateCounterpartyBankResponse();
  let onInvalidAccountNumberPasted;
  if (vm instanceof CustomerRefundCandidateDetailViewModel) {
    onInvalidAccountNumberPasted = vm.onInvalidAccountNumberPasted;
  }

  return (
    <>
      <Form.Row>
        <FormGroup as={Col} lg={4} target={refundPayment} property="paymentType">
          <FormLabel>{vm.translateSubAttribute("customerRefundCandidateRefundPaymentResponse", "paymentType")}</FormLabel>
          <AdvancedSelect items={vm.transferTypeOptions} disabled={!vm.isPaymentEditable} />
        </FormGroup>
        {refundPayment.paymentType === RefundPaymentType.ForeignTransfer && (
          <FormGroup as={Col} lg={8} target={counterpartyBank} property="codeType">
            <FormLabel>{vm.translateSubAttribute("customerRefundCandidateCounterpartyBankResponse", "codeType")}</FormLabel>
            <AdvancedSelect allowEmpty items={vm.bankCodeOptions} disabled={!vm.isPaymentEditable} />
          </FormGroup>
        )}
        {paymentType === RefundPaymentType.DomesticTransfer && (
          <FormGroup as={Col} lg={8} target={refundPayment} property="counterpartyBank">
            <FormLabel required={true}>
              {vm.translateSubAttribute("customerRefundCandidateRefundPaymentResponse", "counterpartyBank")}
            </FormLabel>
            <CzechAccountInput onInvalidValuePasted={onInvalidAccountNumberPasted} disabled={!vm.isPaymentEditable} />
          </FormGroup>
        )}
      </Form.Row>
      {(paymentType === RefundPaymentType.ForeignTransfer || paymentType === RefundPaymentType.SepaTransfer) && (
        <Form.Row>
          {paymentType === RefundPaymentType.SepaTransfer ? (
            <FormGroup as={Col} md={8} target={counterpartyBank} property="accountNumber">
              <FormLabel>{translateGeneral("finance.iban")}</FormLabel>
              <IbanInput onInvalidValuePasted={onInvalidAccountNumberPasted} disabled={!vm.isPaymentEditable} />
            </FormGroup>
          ) : (
            <FormGroup as={Col} md={8} target={counterpartyBank} property="accountNumber">
              <FormLabel>
                {vm.translateSubAttribute("customerRefundCandidateRefundPaymentResponse", "counterpartyBank")}
              </FormLabel>
              <BaseAccountNumberInput target={counterpartyBank} property="accountNumber" disabled={!vm.isPaymentEditable} />
            </FormGroup>
          )}
          <BankCodeInputGroup
            md={4}
            codeType={counterpartyBank.codeType}
            target={counterpartyBank}
            disabled={!vm.isPaymentEditable}
          />
        </Form.Row>
      )}
      {paymentType === RefundPaymentType.ForeignTransfer && (
        <Form.Row>
          <FormGroup as={Col} sm={12} target={counterpartyBank} property="name">
            <FormLabel>{vm.translateSubAttribute("customerRefundCandidateCounterpartyBankResponse", "name")}</FormLabel>
            <Input disabled={!vm.isPaymentEditable} />
          </FormGroup>
          <FormGroup as={Col} sm={12} target={counterpartyBank} property="cityAddress">
            <FormLabel>{vm.translateSubAttribute("customerRefundCandidateCounterpartyBankResponse", "cityAddress")}</FormLabel>
            <Input disabled={!vm.isPaymentEditable} />
          </FormGroup>
          <FormGroup as={Col} sm={12} target={counterpartyBank} property="countryId">
            <FormLabel>{vm.translateSubAttribute("customerRefundCandidateCounterpartyBankResponse", "countryId")}</FormLabel>
            <CountrySelect vm={vm.countrySelectViewModel} disabled={!vm.isPaymentEditable} allowEmpty={true} />
          </FormGroup>
          <PossibleSEPAPaymentWarning show={vm.showSEPAWarning} />
        </Form.Row>
      )}
      {(paymentType === RefundPaymentType.ForeignTransfer || paymentType === RefundPaymentType.SepaTransfer) && (
        <>
          <hr />
          <Form.Row>
            <FormGroup as={Col} xl={6} target={refundPayment} property="counterpartyName">
              <FormLabel>
                {vm.translateSubAttribute("customerRefundCandidateRefundPaymentResponse", "counterpartyName")}
              </FormLabel>
              <Input disabled={!vm.isPaymentEditable} />
            </FormGroup>
            <FormGroup as={Col} xl={6} target={refundPayment} property="counterpartyCountryId">
              <FormLabel>
                {vm.translateSubAttribute("customerRefundCandidateRefundPaymentResponse", "counterpartyCountryId")}
              </FormLabel>
              <CountrySelect
                vm={vm.countrySelectViewModel}
                disabled={!vm.isPaymentEditable}
                allowEmpty={true}
                onlySEPACountries={vm.isSEPAPayment}
              />
            </FormGroup>
            <FormGroup as={Col} xl={6} target={refundPayment} property="counterpartyStreetAddress">
              <FormLabel>
                {vm.translateSubAttribute("customerRefundCandidateRefundPaymentResponse", "counterpartyStreetAddress")}
              </FormLabel>
              <Input disabled={!vm.isPaymentEditable} />
            </FormGroup>
            <FormGroup as={Col} xl={6} target={refundPayment} property="counterpartyCityAddress">
              <FormLabel>
                {vm.translateSubAttribute("customerRefundCandidateRefundPaymentResponse", "counterpartyCityAddress")}
              </FormLabel>
              <Input disabled={!vm.isPaymentEditable} />
            </FormGroup>
          </Form.Row>
          <hr />
        </>
      )}
      <Form.Row>
        <Form.Group as={Col} xl={4}>
          <Form.Label>{translateEnum("feeType", FeeType.ChargeRefundFee)}</Form.Label>
          <FakeInput target={vm} property="chargeRefundFee" valueFormatter={formatAmount} />
        </Form.Group>
        <Form.Group as={Col} xl={8}>
          <Check
            className="mt-6 mb-3"
            target={vm.item}
            property="isWithoutRefundFee"
            disabled={!vm.isEditable || !vm.isUserAllowedToEdit}
            custom
            label={<span>{vm.translateAttribute("isWithoutRefundFee")}</span>}
          />
        </Form.Group>
      </Form.Row>
      <Form.Row>
        <Form.Group as={Col} xl={4}>
          <Form.Label>{vm.translate("bankFees")}</Form.Label>
          <FakeInput target={vm} property="bankFees" valueFormatter={formatAmount} />
        </Form.Group>
        <Form.Group as={Col} xl={4}>
          <Form.Label>{vm.translateAttribute("refundAmount")}</Form.Label>
          <FakeInput target={vm.item} property="refundAmount" valueFormatter={formatAmount} />
        </Form.Group>
        <Form.Group as={Col} xl={4}>
          <Form.Label>{vm.translateAttribute("officialRecordId")}</Form.Label>
          <FakeInput target={vm.item} property="officialRecordId" />
        </Form.Group>
      </Form.Row>
      <Form.Row>
        <FormGroup as={Col} sm={12} target={refundPayment} property="additionalAvMessage">
          <FormLabel>{vm.translateSubAttribute("customerRefundCandidateRefundPaymentResponse", "additionalAvMessage")}</FormLabel>
          <Input disabled={!vm.isPaymentEditable} />
        </FormGroup>
      </Form.Row>
      <Form.Row>
        <Form.Group as={Col} xl={8}>
          <Form.Label>{vm.translateAttribute("refundSenderBankAccount")}</Form.Label>
          <FakeInput target={vm.item} property="refundSenderBankAccount" valueFormatter={formatBankAccount} />
        </Form.Group>
      </Form.Row>
    </>
  );
});

export default BankTransferRefundSection;
